import React, { useEffect, useState } from "react";
import { getContrastTextColor } from "../../../../utils/styles/getLuminace";
import "./UiButton.scss";

type FontSize = "xxs" | "xs" | "s" | "m" | "l" | "xl" | "xxl";

type IconElement = React.ReactElement<{ style?: React.CSSProperties }>;

type Props = {
  labelStyles?: React.CSSProperties;
  link?: boolean;
  outline?: boolean;
  loading?: boolean;
  blink?: boolean; // Makes the button blink (Default: false)
  leftIcon?: IconElement;
  rightIcon?: IconElement;
  color?: string;
  fontSize?: FontSize;
  disabled?: boolean;
  children?: string | React.ReactNode;
  shadow?: boolean; // Adds a subtle shadow to the button (Default: false)
  minWidth?: number | string;
  minHeight?: number | string;
  maxWidth?: number | string;
  maxHeight?: number | string;
  borderRadius?: number | string; // Custom border-radius (Default: 10px from SCSS)
  style?: React.CSSProperties;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

/**
 * @description
 * Universal button component
 *
 * @example
 * <UiButton onClick={handleClick}>Click me</UiButton>
 * <UiButton link color="#007bff">Link Button</UiButton>
 * <UiButton outline color="#28a745">Outline Button</UiButton>
 * <UiButton loading>Loading Button</UiButton>
 * <UiButton blink>Blinking Button</UiButton>
 * <UiButton leftIcon={<Icon />} rightIcon={<Icon />}>Button with Icons</UiButton>
 * <UiButton fontSize="l" shadow>Large Shadow Button</UiButton>
 * <UiButton minWidth={100} minHeight={50} maxWidth={200} maxHeight={100}>Sized Button</UiButton>
 * <UiButton borderRadius="50%">Round Button</UiButton>
 */
export default function UiButton({
  style,
  labelStyles,
  link = false,
  outline = false,
  loading = false,
  blink = false,
  leftIcon,
  rightIcon,
  color,
  children,
  disabled,
  fontSize = "m",
  shadow = false,
  minWidth,
  minHeight,
  maxWidth,
  maxHeight,
  borderRadius,
  onClick,
  className,
  ...props
}: Props) {
  const [blinkOn, setBlinkOn] = useState(true);

  useEffect(() => {
    if (!blink) return;
    const interval = setInterval(() => setBlinkOn((prev) => !prev), 500);
    return () => clearInterval(interval);
  }, [blink]);

  const accentColor = color;

  const textColor = accentColor
    ? link || outline
      ? accentColor
      : getContrastTextColor(accentColor)
    : undefined;

  const buttonClassName = [
    "inb-ui-button",
    `inb-ui-button--${fontSize}`,
    link && "inb-ui-button--link",
    !link && outline && "inb-ui-button--outline",
    shadow && "inb-ui-button--shadow",
    blink && "inb-ui-button--blink",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const buttonStyle: React.CSSProperties = {
    ...(accentColor && link && { color: accentColor }),
    ...(accentColor && !link && outline && { borderColor: accentColor }),
    ...(accentColor && !link && !outline && { backgroundColor: accentColor }),
    ...(blink && { opacity: blinkOn ? 1 : 0.3 }),
    ...(minWidth !== undefined && { minWidth }),
    ...(minHeight !== undefined && { minHeight }),
    ...(maxWidth !== undefined && { maxWidth }),
    ...(maxHeight !== undefined && { maxHeight }),
    ...(borderRadius !== undefined && { borderRadius }),
    ...style,
  };

  return (
    <button
      type="button"
      className={buttonClassName}
      style={buttonStyle}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      onClick={onClick}
      {...props}
    >
      {loading && (
        <span className="inb-ui-button-spinner" aria-hidden="true">
          <img
            className="rolling-loader"
            src="assets/img/rolling_loader.svg"
            alt="loading-spinner"
          />
        </span>
      )}
      <span
        className="inb-ui-button-content"
        style={loading ? { visibility: "hidden" } : undefined}
      >
        {leftIcon &&
          React.cloneElement(leftIcon, {
            style: {
              ...leftIcon.props.style,
              ...(textColor ? { color: textColor } : {}),
            },
          })}
        {children && (
          <span style={{ ...(textColor ? { color: textColor } : {}), ...labelStyles }}>
            {children}
          </span>
        )}
        {rightIcon &&
          React.cloneElement(rightIcon, {
            style: {
              ...rightIcon.props.style,
              ...(textColor ? { color: textColor } : {}),
            },
          })}
      </span>
    </button>
  );
}
